import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import BusinessCard from './components/BusinessCard';
import FilterPanel from './components/FilterPanel';
import FeaturedInitiative from './components/FeaturedInitiative';

const BusinessESG = () => {
  const [businesses, setBusinesses] = useState([]);
  const [filteredBusinesses, setFilteredBusinesses] = useState([]);
  const [filters, setFilters] = useState({
    industry: 'all',
    esgFocus: 'all',
    companySize: 'all',
    location: 'all',
    minRating: 0
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  // Mock data for business profiles
  useEffect(() => {
    const mockBusinesses = [
      {
        id: 1,
        name: 'GreenTech Solutions',
        logo: 'https://images.unsplash.com/photo-1560179707-f14e90ef3623?w=200',
        logoAlt: 'GreenTech Solutions company logo with green leaf icon',
        industry: 'Technology',
        esgScore: 92,
        esgFocus: ['Environmental', 'Governance'],
        companySize: 'Large',
        location: 'California, USA',
        description: 'Leading the way in sustainable technology solutions with carbon-neutral operations',
        recentInitiatives: [
          'Achieved 100% renewable energy across all facilities',
          'Launched $10M fund for climate tech startups'
        ],
        sustainabilityGoals: ['Net-zero by 2030', 'Zero waste production'],
        communityEngagement: 1250,
        stakeholderRating: 4.8
      },
      {
        id: 2,
        name: 'Ethical Fashion Co',
        logo: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=200',
        logoAlt: 'Ethical Fashion Co logo with sustainable fabric symbol',
        industry: 'Retail',
        esgScore: 88,
        esgFocus: ['Social', 'Environmental'],
        companySize: 'Medium',
        location: 'New York, USA',
        description: 'Fair trade fashion brand committed to ethical manufacturing and worker welfare',
        recentInitiatives: [
          'Implemented living wage policy for all suppliers',
          'Reduced water usage by 60% in production'
        ],
        sustainabilityGoals: ['100% sustainable materials by 2025', 'Fair wages globally'],
        communityEngagement: 890,
        stakeholderRating: 4.7
      },
      {
        id: 3,
        name: 'HealthFirst Pharmaceuticals',
        logo: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=200',
        logoAlt: 'HealthFirst Pharmaceuticals logo with medical cross emblem',
        industry: 'Healthcare',
        esgScore: 85,
        esgFocus: ['Social', 'Governance'],
        companySize: 'Large',
        location: 'Massachusetts, USA',
        description: 'Accessible healthcare through equitable drug pricing and community health programs',
        recentInitiatives: [
          'Donated $50M in medications to underserved communities',
          'Established diversity council for inclusive research'
        ],
        sustainabilityGoals: ['Universal access to essential medicines', 'Diverse clinical trials'],
        communityEngagement: 2100,
        stakeholderRating: 4.9
      },
      {
        id: 4,
        name: 'CleanEnergy Corp',
        logo: 'https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?w=200',
        logoAlt: 'CleanEnergy Corp logo with solar panel icon',
        industry: 'Energy',
        esgScore: 94,
        esgFocus: ['Environmental'],
        companySize: 'Large',
        location: 'Texas, USA',
        description: 'Renewable energy pioneer transforming power infrastructure globally',
        recentInitiatives: [
          'Installed 500MW of solar capacity in developing nations',
          'Created 10,000 green jobs in rural communities'
        ],
        sustainabilityGoals: ['1GW renewable capacity by 2026', 'Zero emissions grid'],
        communityEngagement: 1680,
        stakeholderRating: 4.8
      },
      {
        id: 5,
        name: 'FairBank Financial',
        logo: 'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=200',
        logoAlt: 'FairBank Financial logo with handshake symbol',
        industry: 'Finance',
        esgScore: 86,
        esgFocus: ['Social', 'Governance'],
        companySize: 'Large',
        location: 'London, UK',
        description: 'Ethical banking with transparent practices and community investment focus',
        recentInitiatives: [
          '$100M committed to minority-owned business loans',
          'Divested from fossil fuel projects'
        ],
        sustainabilityGoals: ['Green bonds portfolio', 'Financial inclusion programs'],
        communityEngagement: 1520,
        stakeholderRating: 4.6
      },
      {
        id: 6,
        name: 'EcoPackaging Inc',
        logo: 'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=200',
        logoAlt: 'EcoPackaging Inc logo with recycling arrows',
        industry: 'Manufacturing',
        esgScore: 90,
        esgFocus: ['Environmental'],
        companySize: 'Medium',
        location: 'Oregon, USA',
        description: 'Biodegradable packaging solutions reducing plastic waste globally',
        recentInitiatives: [
          'Eliminated 50 million plastic containers from supply chain',
          'Partnered with ocean cleanup initiatives'
        ],
        sustainabilityGoals: ['100% compostable products', 'Circular economy model'],
        communityEngagement: 720,
        stakeholderRating: 4.7
      }
    ];

    setTimeout(() => {
      setBusinesses(mockBusinesses);
      setFilteredBusinesses(mockBusinesses);
      setIsLoading(false);
    }, 500);
  }, []);

  // Filter and search logic
  useEffect(() => {
    let filtered = [...businesses];

    // Apply search
    if (searchQuery) {
      filtered = filtered?.filter(business =>
        business?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        business?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        business?.recentInitiatives?.some(initiative => 
          initiative?.toLowerCase()?.includes(searchQuery?.toLowerCase())
        )
      );
    }

    // Apply filters
    if (filters?.industry !== 'all') {
      filtered = filtered?.filter(b => b?.industry === filters?.industry);
    }
    if (filters?.esgFocus !== 'all') {
      filtered = filtered?.filter(b => b?.esgFocus?.includes(filters?.esgFocus));
    }
    if (filters?.companySize !== 'all') {
      filtered = filtered?.filter(b => b?.companySize === filters?.companySize);
    }
    if (filters?.location !== 'all') {
      filtered = filtered?.filter(b => b?.location?.includes(filters?.location));
    }
    if (filters?.minRating > 0) {
      filtered = filtered?.filter(b => b?.esgScore >= filters?.minRating);
    }

    setFilteredBusinesses(filtered);
  }, [businesses, filters, searchQuery]);

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const handleResetFilters = () => {
    setFilters({
      industry: 'all',
      esgFocus: 'all',
      companySize: 'all',
      location: 'all',
      minRating: 0
    });
    setSearchQuery('');
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-primary/10 via-background to-accent/5 border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl sm:text-5xl font-bold text-foreground mb-4">
              Business ESG Profiles
            </h1>
            <p className="text-lg text-muted-foreground max-w-3xl">
              Discover corporate environmental, social, and governance initiatives. Explore transparency in sustainability goals, diversity programs, and community impact.
            </p>
          </motion.div>

          {/* Search Bar */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mt-8"
          >
            <div className="relative max-w-2xl">
              <Icon 
                name="Search" 
                size={20} 
                className="absolute left-4 top-1/2 transform -translate-y-1/2 text-muted-foreground"
              />
              <input
                type="text"
                placeholder="Search companies, initiatives, or commitment areas..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e?.target?.value)}
                className="w-full pl-12 pr-4 py-4 text-base border border-border rounded-xl bg-background focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent shadow-sm"
              />
            </div>
          </motion.div>
        </div>
      </div>
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Filters Sidebar */}
          <div className="lg:col-span-1">
            <FilterPanel 
              filters={filters}
              onFilterChange={handleFilterChange}
              onReset={handleResetFilters}
            />
          </div>

          {/* Business Grid */}
          <div className="lg:col-span-3">
            {/* Featured Initiative */}
            <FeaturedInitiative />

            {/* Results Header */}
            <div className="flex items-center justify-between mb-6 mt-8">
              <h2 className="text-xl font-semibold text-foreground">
                {filteredBusinesses?.length} {filteredBusinesses?.length === 1 ? 'Business' : 'Businesses'} Found
              </h2>
            </div>

            {/* Business Cards Grid */}
            {isLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {[1, 2, 3, 4]?.map(i => (
                  <div key={i} className="bg-card border border-border rounded-xl p-6 animate-pulse">
                    <div className="h-16 w-16 bg-muted rounded-lg mb-4" />
                    <div className="h-6 bg-muted rounded w-3/4 mb-3" />
                    <div className="h-4 bg-muted rounded w-full mb-2" />
                    <div className="h-4 bg-muted rounded w-5/6" />
                  </div>
                ))}
              </div>
            ) : filteredBusinesses?.length === 0 ? (
              <div className="text-center py-16">
                <Icon name="Building2" size={64} className="mx-auto text-muted-foreground mb-4" />
                <h3 className="text-xl font-semibold text-foreground mb-2">No businesses found</h3>
                <p className="text-muted-foreground mb-6">
                  Try adjusting your filters or search query
                </p>
                <button
                  onClick={handleResetFilters}
                  className="px-6 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
                >
                  Reset Filters
                </button>
              </div>
            ) : (
              <motion.div 
                className="grid grid-cols-1 md:grid-cols-2 gap-6"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
              >
                {filteredBusinesses?.map((business, index) => (
                  <motion.div
                    key={business?.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                  >
                    <BusinessCard business={business} />
                  </motion.div>
                ))}
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessESG;